import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "360",
  "slug": "true-color-vs-false-color-satellite-imagery",
  "title": "What Do the Colors in a Satellite Image Mean? True Color vs False Color",
  "excerpt": "Why does vegetation glow red in some satellite images? Because satellites see far more than your eyes do. A plain-English guide to true color, false color, and the invisible light that makes imagery so useful.",
  "date": "2026-06-05",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1514864151880-d1bef4892f29?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjBmYXJtbGFuZCUyMGZpZWxkcyUyMHBhdHRlcm58ZW58MXwwfHx8MTc4MDY0NzE1OHww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "True-color images map the satellite's red, green, and blue bands to look roughly the way your eyes would see the scene. False-color images swap in invisible bands — most often near-infrared — to reveal things the eye cannot, which is why healthy vegetation often appears bright red. Both come from the same satellite, just combined differently.",
  "content": [
    {
      "type": "paragraph",
      "text": "If you have ever seen a satellite image where forests blaze bright red and rivers are inky black, you might have assumed it was artistic license. It is not. That image is showing you light your eyes cannot see, translated into colors you can. Understanding the difference between true-color and false-color imagery is one of the most useful things you can learn about how we look at Earth from above."
    },
    {
      "type": "heading",
      "text": "Satellites See More Than You Do"
    },
    {
      "type": "paragraph",
      "text": "Your eyes detect a narrow slice of light — the visible spectrum, from red through to violet. Earth-observation satellites like Landsat and the Sentinel series detect that visible light too, but they also capture bands well beyond it, especially near-infrared and shortwave infrared. Each satellite records several separate bands, each one a grayscale measurement of how much light of a particular wavelength a surface reflected. Color images are built afterward by assigning bands to the red, green, and blue channels of a picture."
    },
    {
      "type": "heading",
      "text": "True Color: The World as You'd See It"
    },
    {
      "type": "paragraph",
      "text": "A true-color (or natural-color) image assigns the satellite's red band to red, its green band to green, and its blue band to blue. The result looks roughly the way the scene would look from an airplane window: blue oceans, green forests, tan deserts, white clouds and snow. True color is intuitive and great for general orientation, which is exactly why a game built around recognizing real places — like EarthGuessr — leans on natural-looking imagery. You read it with the same instincts you use to look out a window."
    },
    {
      "type": "heading",
      "text": "False Color: Swapping in the Invisible"
    },
    {
      "type": "paragraph",
      "text": "A false-color image deliberately breaks the natural mapping, feeding an invisible band into one of the color channels. The most common combination puts near-infrared light into the red channel. The reason this is so useful comes down to one fact of biology: healthy, living vegetation reflects near-infrared light very strongly — far more than it reflects visible green."
    },
    {
      "type": "paragraph",
      "text": "So in a classic false-color image, thriving plants light up in vivid red or pink, because the near-infrared they bounce back has been painted red. Bare soil shows up in tans and browns, water turns dark blue or black, and cities appear grey or blue-grey. Nothing has been invented; the satellite simply showed you a slice of reality your eyes are blind to."
    },
    {
      "type": "heading",
      "text": "Why Vegetation Is the Star"
    },
    {
      "type": "paragraph",
      "text": "That near-infrared signal is so reliable that scientists built a whole index around it: the Normalized Difference Vegetation Index, or NDVI. It compares how much near-infrared a surface reflects against how much red it absorbs. Dense, healthy vegetation scores high; bare ground, water, and dying plants score low. NDVI lets analysts track crop health, drought, deforestation, and the changing of the seasons — all from the contrast between two bands."
    },
    {
      "type": "heading",
      "text": "Other Bands, Other Jobs"
    },
    {
      "type": "paragraph",
      "text": "Near-infrared is not the only invisible tool. Shortwave infrared helps distinguish snow from cloud (they look identical in true color but very different in shortwave infrared) and reveals burn scars after wildfires, since charred land has a distinct signature. Thermal bands measure heat, useful for spotting active fires or mapping urban heat. Each band answers a different question, and false-color combinations are simply ways of asking those questions visually."
    },
    {
      "type": "heading",
      "text": "A Note on Pansharpening"
    },
    {
      "type": "paragraph",
      "text": "One more trick worth knowing: many satellites carry a high-resolution panchromatic band — a sharp grayscale image — alongside lower-resolution color bands. Combining the two, a process called pansharpening, produces an image that is both sharp and colorful. So the crisp, vivid satellite views you see online are often the product of several bands blended together, not a single straight photograph."
    },
    {
      "type": "heading",
      "text": "Reading Color Like a Pro"
    },
    {
      "type": "paragraph",
      "text": "Once you know the rules, color becomes information. In true color, trust your eyes. In false colour, check the band assignment first. Red can represent vegetation in a near-infrared display, while dark pixels may be water, shadow, or another low-reflectance surface. There is no universal colour key. Recognizing which kind of image you are looking at is the first step to reading it correctly."
    }
  ]
};
